import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { Notification, NotificationContextType, NotificationType } from '@/types/notifications';

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

type NotificationAction =
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'REMOVE_NOTIFICATION'; payload: string }
  | { type: 'CLEAR_ALL' }
  | { type: 'CLEAR_BY_TYPE'; payload: NotificationType };

interface NotificationState {
  notifications: Notification[];
}

const notificationReducer = (state: NotificationState, action: NotificationAction): NotificationState => {
  switch (action.type) {
    case 'ADD_NOTIFICATION':
      // Check for duplicate notifications (same title and type)
      const isDuplicate = state.notifications.some(
        notification => 
          notification.title === action.payload.title && 
          notification.type === action.payload.type
      );
      
      if (isDuplicate) {
        return state;
      }

      // Sort by priority (higher priority first) and creation time
      const newNotifications = [...state.notifications, action.payload]
        .sort((a, b) => {
          const priorityDiff = (b.priority || 0) - (a.priority || 0);
          if (priorityDiff !== 0) return priorityDiff;
          return b.createdAt.getTime() - a.createdAt.getTime();
        });

      return {
        ...state,
        notifications: newNotifications,
      };

    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload),
      };

    case 'CLEAR_ALL':
      return {
        ...state,
        notifications: [],
      };

    case 'CLEAR_BY_TYPE':
      return {
        ...state,
        notifications: state.notifications.filter(n => n.type !== action.payload),
      };

    default:
      return state;
  }
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, { notifications: [] });

  const addNotification = useCallback((notificationData: Omit<Notification, 'id' | 'createdAt'>): string => {
    const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const notification: Notification = {
      id,
      createdAt: new Date(),
      duration: 3500, // Changed to 3.5 seconds for better UX
      position: 'top-right',
      priority: getPriorityByType(notificationData.type),
      showProgress: true,
      dismissible: false, // Disabled manual dismissal
      ...notificationData,
    };

    dispatch({ type: 'ADD_NOTIFICATION', payload: notification });

    // Auto-dismiss (handled by NotificationItem component now)
    return id;
  }, []);

  const removeNotification = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_NOTIFICATION', payload: id });
  }, []);

  const clearAll = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL' });
  }, []);

  const clearByType = useCallback((type: NotificationType) => {
    dispatch({ type: 'CLEAR_BY_TYPE', payload: type });
  }, []);

  const value: NotificationContextType = {
    notifications: state.notifications,
    addNotification,
    removeNotification,
    clearAll,
    clearByType,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

const getPriorityByType = (type: NotificationType): number => {
  switch (type) {
    case 'error': return 4;
    case 'warning': return 3;
    case 'success': return 2;
    case 'info': return 1;
    default: return 0;
  }
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
